import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useCatalogoApi } from '../catalogo/api'
import { t, Lang } from './i18n'
import { EmptyState } from '../styles/EmptyState'
import { Spinner } from '../styles/Spinner'
import styles from './PanelListas.module.css'

export interface TarjetasJugador {
  miembro_equipo_id: string
  nombre: string
  alias: string | null
  equipo: { id: string; nombre: string; categoria: string }
  situacion: 'sancionado' | 'apercibido'
  amarillas_total: number
  amarillas_ciclo: number
  rojas_total: number
  ultima_jornada: number
  proxima_jornada: { numero: number; fecha: string | null; rival: string | null } | null
}

const SECCIONES = [
  { situacion: 'sancionado', titulo: 'sancionados', pill: 'pill pill-danger' },
  { situacion: 'apercibido', titulo: 'apercibidos', pill: 'pill pill-warning' },
] as const

// Issue #85 (club, 2026-10-23): "En Tarjetas aparecen los apercibidos, es decir, a los que les queda una
// para cumplir ciclo, y los ya sancionados, siempre con nombre, equipo y situación, y todo actualizado
// con los registros del sistema". Datos de GET /panel/tarjetas (mismo ciclo de 5 amarillas que el
// minutaje, #62; solo F11, que es donde se registran las tarjetas).
export function PanelTarjetasTab({ temporadaId, lang, hayF11 }: { temporadaId: string; lang: Lang; hayF11: boolean }) {
  const api = useCatalogoApi()
  const [tarjetas, setTarjetas] = useState<TarjetasJugador[] | null>(null)

  useEffect(() => {
    setTarjetas(null)
    api.get(`/panel/tarjetas?temporada_id=${temporadaId}`).then(setTarjetas)
  }, [temporadaId])

  if (!hayF11) return <EmptyState text={t('sinEquiposF11', lang)} icon="🛡️" />
  if (tarjetas === null) return <Spinner />

  return (
    <div className={styles.contenedor}>
      <p className={styles.ayuda}>{t('tarjetasAyuda', lang)}</p>
      {tarjetas.length === 0 && <EmptyState text={t('sinTarjetas', lang)} icon="✅" />}
      {SECCIONES.map(({ situacion, titulo, pill }) => {
        const lista = tarjetas.filter((j) => j.situacion === situacion)
        if (lista.length === 0) return null
        return (
          <section key={situacion} className={styles.seccion}>
            <h3>
              {t(titulo, lang)} <span className={styles.contador}>{lista.length}</span>
            </h3>
            <div className="table-scroll">
              <table className="sticky-first-col" aria-label={t(titulo, lang)}>
                <thead>
                  <tr>
                    <th>{t('jugador', lang)}</th>
                    <th>{t('equipo', lang)}</th>
                    <th>{t('situacion', lang)}</th>
                    <th>{t('amarillas', lang)}</th>
                    <th>{t('rojas', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((j) => (
                    <tr key={j.miembro_equipo_id}>
                      <td>{j.nombre}</td>
                      <td>
                        <Link to={`/equipos/${j.equipo.id}`} className={styles.equipo} data-categoria={j.equipo.categoria}>
                          {j.equipo.nombre}
                        </Link>
                      </td>
                      <td>
                        <span className={pill}>{t(situacion, lang)}</span>
                        <small className={styles.detalle}>
                          {situacion === 'sancionado'
                            ? t('cumpleEn', lang).replace('{n}', String(j.proxima_jornada?.numero ?? j.ultima_jornada + 1)) +
                              (j.proxima_jornada?.rival ? ` · ${j.proxima_jornada.rival}` : '')
                            : t('amarillasCiclo', lang).replace('{n}', String(j.amarillas_ciclo))}
                        </small>
                      </td>
                      <td>🟨 {j.amarillas_total}</td>
                      <td>🟥 {j.rojas_total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )
      })}
    </div>
  )
}
